import {AxiosError, AxiosInstance, AxiosResponse} from "axios";
import {signOut} from "next-auth/react";

/**
 * NOTE: Currently we're not using redux in this project.
 * The type of this will be changed to the proper
 * redux store type once we start using redux.
 */
// import type {StoreType} from "@store/index";
type StoreType = any;

import {httpStatusCodes} from "../../../../customTypes/NetworkTypes";

// Guards against firing multiple sign-outs when several requests 401 at once.
let isSigningOut = false;

/**
 * Mapping all the responseInterceptors defined as closures inside this function
 * to the axios-instance apiServer.
 *
 * Defined responseInterceptor closures needs to be registered to the constant object
 * axiosResponseInterceptors in order to get mapped.
 *
 * @param store
 * @param apiServer
 */
function cresivoxServerAxiosResponseInterceptors(
	store: StoreType | null,
	apiServer: AxiosInstance,
): void {
	/*
		Response Interceptor which determines whether the error caused by authorization
		token expiration or not in case of an error reason.

		If yes, it attempts to get a new authorization token issued by invoking the
		designated api. If the refresh attempt is successful, then it will retry the
		originally failed request with the newly issued authorization token.
	 */
	const responseAuthTokenExpireInterceptor = {
		onFulfilled: async (
			response: AxiosResponse,
		): Promise<AxiosResponse<any, any>> => {
			return response;
		},

		onRejected: async (reason: AxiosError): Promise<unknown> => {
			if (store && reason && reason.response) {
				const {status: errorHttpStatus} = reason.response;

				/*
					Returns any error that is not a type of httpStatusCodes.CLIENT_ERROR_UNAUTHORIZED
				*/
				if (errorHttpStatus !== httpStatusCodes.CLIENT_ERROR_UNAUTHORIZED) {
					return new Promise((resolve, reject) => {
						reject(reason);
					});
				}

				/*
					The backend returns 401 for both a missing and an expired/invalid token —
					there's no refresh token flow, so the only correct move is to sign the
					user out and send them back to /signin instead of leaving the panel in a
					silently-broken state.
				*/
				if (!isSigningOut && typeof window !== "undefined") {
					isSigningOut = true;
					signOut({ callbackUrl: `${window.location.origin}/signin` }).finally(() => {
						isSigningOut = false;
					});
				}
			}

			return new Promise((resolve, reject) => {
				reject(reason);
			});
		},
	};

	// TODO: add responseNetworkFailureInterceptor

	/**
	 * All response-interceptors should be registered here.
	 *
	 * eg: {
	 *     responseAuthorizationTokenExpireInterceptor: responseAuthorizationTokenExpireInterceptor
	 * }
	 */
	const axiosResponseInterceptors = {
		responseAuthTokenExpireInterceptor,
	};

	/**
	 * Extracting and mapping each responseInterceptor defined inside
	 * AxiosInterceptors.responseInterceptors with the axios-instance
	 * apiServer.
	 */
	Object.values(axiosResponseInterceptors).forEach(
		(responseInterceptor: any) => {
			apiServer.interceptors.response.use(
				responseInterceptor.onFulfilled,
				responseInterceptor.onRejected,
			);
		},
	);
}

export default cresivoxServerAxiosResponseInterceptors;
